import React, { useEffect, useState } from 'react';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import axios from 'axios';
import { DocumentContainer } from './documentDisplay';
import { ChatContainer } from './chatSection';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getUrl } from '../utils';
import { useCallback } from 'react';
import { alert, AlertType } from '@quyva/shared-ui';
import { Config } from '../config/envConfig';
import { francAll } from 'franc';
import {
    AuthStatus,
    RouterPath,
    ErrorType,
    supportedLanguages,
    splitTextIntoChunks,
    htmlToPlainText,
    removeUuidPrefix,
    selectionModeCode,
    sortChunks,
    SortSameDocument,
    ErrorMessages,
    ChatFormatType,
} from '@quyva/common';

const MAX_CHUNK_LENGTH = 700;

// Map franc ISO-639-3 codes to @quyva/common supportedLanguages keys
// supportedLanguages keys: हिन्दी, मराठी, ENGLISH_UK, ENGLISH_US
const francToSupportedLanguageKeyMap = {
    hin: 'हिन्दी',
    mar: 'मराठी',
    eng: 'ENGLISH_US',
};

const detectLangKeyFromText = (text) => {
    if (!text || typeof text !== 'string') return null;
    const cleaned = text.trim();
    if (cleaned.length < 10) return null;

    try {
        const codes = francAll(cleaned, { minLength: 10 });
        const top = Array.isArray(codes) && codes.length ? codes[0] : null;
        const langCode = Array.isArray(top) ? top[0] : top;
        if (!langCode || langCode === 'und') return null;
        const mapped = francToSupportedLanguageKeyMap[langCode];
        if (mapped && supportedLanguages[mapped]) return mapped;
        return null;
    } catch (e) {
        return null;
    }
};

const logout = (
    vendorId,
    setIsGenerating,
    setAuthState,
    navigate,
    enableAudioFeature
) => {
    alert(
        AlertType.WARNING,
        'Session expired. Please verify again to continue.'
    );
    setIsGenerating(false);

    // Avoid showing multiple popups: if we are redirecting due to session expiry,
    // suppress recaptcha error alerts on the captcha screen and only log them.
    sessionStorage.setItem('suppressRecaptchaErrorAlert', 'true');

    // Clear session ID on logout
    sessionStorage.removeItem('sessionId');

    // Get productSupport data from localStorage
    const productSupport = JSON.parse(localStorage.getItem('productSupport'));
    const foundVendor = productSupport[vendorId];
    if (foundVendor) {
        productSupport[vendorId] = {
            ...foundVendor,
            token: null,
        };

        localStorage.setItem('productSupport', JSON.stringify(productSupport));
    }

    setAuthState(AuthStatus.NOT_AUTHENTICATED);

    const queryParams = [];
    if (enableAudioFeature) queryParams.push('audio=true');
    const queryString =
        queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    const path = `/${RouterPath.VERIFY_CAPTCHA}/${vendorId}${queryString}`;
    navigate(path);
};

const fetchTTSResponse = async (text, language) => {
    try {
        const lang = supportedLanguages[language];
        const ttsUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/tts`
        );

        const response = await axios.get(ttsUrl, {
            params: { text, language: lang },
            responseType: 'blob', // Important for binary audio response
        });

        if (response) {
            const ttsBlob = new Blob([response.data], { type: 'audio/mpeg' });
            const audioURL = URL.createObjectURL(ttsBlob);

            return audioURL;
        }
    } catch (error) {
        console.error('TTS API Error:', error);
        throw error;
    }
};

// Splits text, calls TTS API in parallel for chunks, and updates state
export const processTextToAudioChunks = async (text, language) => {
    if (!text.trim()) return;

    if (text.length <= MAX_CHUNK_LENGTH) {
        try {
            const audioURL = await fetchTTSResponse(text, language);
            return [audioURL];
        } catch (error) {
            console.error('Failed to fetch single chunk TTS:', error);
        }
        return;
    }

    const chunks = splitTextIntoChunks(text, language, MAX_CHUNK_LENGTH);

    try {
        // Call all APIs in parallel for faster processing
        const audioUrls = await Promise.allSettled(
            chunks.map((chunk) => fetchTTSResponse(chunk, language))
        );
        const successfulUrls = audioUrls
            .filter((result) => result.status === 'fulfilled')
            .map((result) => result.value);

        if (successfulUrls.length > 0) {
            return successfulUrls;
        } else {
            console.error('All TTS API calls failed!');
        }
    } catch (error) {
        console.error('Error processing audio chunks:', error);
    }
};

const ChatAndDocumentContainer = (props) => {
    const { vendorId } = useParams(); // Extract the vendorId from the URL path
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const {
        selectedProductCategory,
        selectedProduct,
        isSmallScreen,
        enableAudioFeature,
        hasProductCategories,
        hasProducts,
        enableReader,
        setEnableReader,
        enableMusic,
        setEnableMusic,
        enableSpeaker,
        setEnableSpeaker,
        activeAudioId,
        setActiveAudioId,
        audioUrlData,
        setAudioUrlData,
        vendorDetails,
        socket,
    } = props;

    const [query, setQuery] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [currentqna, setcurrentqna] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [questionsData, setQuestionsData] = useState(new Map());
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [abortController, setAbortController] = useState(null);

    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [qnaLoadingText, setQnaLoadingText] = useState('Generating Answer');
    const [activeQnaQid, setActiveQnaQid] = useState(null);
    

    const supportRepoId = vendorDetails?.repoId;
    const supportVirtualRepoId = vendorDetails?.virtualRepoId || null;

    const normalizeMail = (m) => (m || '').toString().trim().toLowerCase();

    const decodeJwtPayload = (token) => {
        try {
            if (!token || typeof token !== 'string') {
                return null;
            }
            const parts = token.split('.');
            if (parts.length < 2) {
                return null;
            }
            const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
            const json = decodeURIComponent(
                atob(padded)
                    .split('')
                    .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join('')
            );
            return JSON.parse(json);
        } catch (e) {
            return null;
        }
    };

    const extractEmailFromProductSupportToken = (token) => {
        try {
            const outer = decodeJwtPayload(token);
            const outerEmail = outer?.email || outer?.userMail || outer?.mail;
            if (outerEmail) {
                return normalizeMail(outerEmail);
            }

            const outerSubEmail = outer?.sub;
            if (outerSubEmail && typeof outerSubEmail === 'string' && outerSubEmail.includes('@')) {
                return normalizeMail(outerSubEmail);
            }

            const userUuidJwt = outer?.user_uuid;
            if (!userUuidJwt) {
                return null;
            }

            const inner = decodeJwtPayload(userUuidJwt);
            const email = inner?.email;
            return normalizeMail(email);
        } catch (e) {
            return null;
        }
    };

    useEffect(() => {
        if (!socket) {
            return;
        }

        const token = props?.user?.token;
        const socketUserMail = extractEmailFromProductSupportToken(token);
        if (!socketUserMail) {
            return;
        }

        const registerClient = () => {
            try {
                socket.emit('registerClient', socketUserMail);
            } catch (e) {
                return;
            }
        };

        const handleQnaUpdate = (data) => {
            try {
                if (!data) {
                    return;
                }

                if (!isGenerating && !activeQnaQid) {
                    return;
                }

                const incomingUserMail = normalizeMail(data.userMail);
                if (incomingUserMail && incomingUserMail !== socketUserMail) {
                    return;
                }

                if (
                    data.repoId &&
                    supportRepoId !== null &&
                    supportRepoId !== undefined &&
                    String(data.repoId) !== String(supportRepoId) &&
                    (supportVirtualRepoId === null || supportVirtualRepoId === undefined || String(data.repoId) !== String(supportVirtualRepoId))
                ) {
                    return;
                }

                if (isGenerating && !activeQnaQid && data.qid) {
                    setActiveQnaQid(data.qid);
                }
                if (activeQnaQid && data.qid && String(activeQnaQid) !== String(data.qid)) {
                    return;
                }

                if (data.message) {
                    setQnaLoadingText(data.message);
                }
            } catch (e) {
                return;
            }
        };

        socket.on('connect', registerClient);
        socket.on('register', registerClient);
        registerClient();

        socket.on('syncUpdate', handleQnaUpdate);
        socket.on('asyncUpdate', handleQnaUpdate);

        return () => {
            socket.off('connect', registerClient);
            socket.off('register', registerClient);
            socket.off('syncUpdate', handleQnaUpdate);
            socket.off('asyncUpdate', handleQnaUpdate);
        };
    }, [socket, props?.user?.token, isGenerating, activeQnaQid, supportRepoId]);

    useEffect(() => {
        // Clear sessionId immediately when component mounts
        sessionStorage.removeItem('sessionId');

        const handleBeforeUnload = () => {
            sessionStorage.removeItem('sessionId');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // ── Greeting injection ────────────────────────────────────────────────────
    // When vendorDetails loads, show the saved greeting as the first bot message
    // before the user types anything. Only fires if an admin has saved a
    // greeting_message in the repository web access settings. No LLM involved.
    useEffect(() => {
        if (!vendorDetails) return;
        const greetingText = (vendorDetails?.greetingMessage || '').trim();
        if (!greetingText) return;

        const greetingQid = `greeting-${Date.now()}`;
        // getDate/getTime in @quyva/common expect "YYYY-MM-DD HH:MM:SS" UTC format
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const greetingTimestamp = `${now.getUTCFullYear()}-${pad(now.getUTCMonth()+1)}-${pad(now.getUTCDate())} ${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
        const greetingEntry = {
            qid: greetingQid,
            question: null,
            answer: greetingText,
            table: null,
            timestamp: greetingTimestamp,
            chunksData: null,
            queries: [],
            productCategory: null,
            product: null,
            sqlQuery: null,
            selectionMode: false,
            attachments: [],
            isGreeting: true,
        };

        questionsData.set(greetingQid, greetingEntry);
        setQuestionsData(new Map(questionsData));
        setActiveQuestion(greetingEntry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorDetails]);
    // ─────────────────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(
        async () => {
            try {
                const isAuthenticated = props.verifyAuthentication(vendorId);
                if (!isAuthenticated) {
                    return;
                }

                if (query === '') {
                    alert(AlertType.WARNING, 'Question cannot be empty');
                    return;
                }

                //e.preventDefault();
                setIsGenerating(true);
                setQnaLoadingText('Searching documents and preparing context');
                setActiveQnaQid(null);

                // Create a new AbortController
                const controller = new AbortController();
                setAbortController(controller); // Save it in state

                const productCategory = selectedProductCategory;
                const product = selectedProduct;
                const effectiveProductCategory =
                    productCategory || (supportRepoId ? 'ALL' : productCategory);
                const effectiveProduct =
                    product || (supportRepoId ? vendorDetails?.webHeading || '' : product);
                let sessionId = sessionStorage.getItem('sessionId');
                if (!sessionId) {
                    try {
                        sessionId =
                            typeof crypto !== 'undefined' && crypto.randomUUID
                                ? crypto.randomUUID()
                                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                    } catch (e) {
                        sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                    }
                    sessionStorage.setItem('sessionId', String(sessionId));
                }
                const virtualRepoId = supportVirtualRepoId;

                const actualQuery = removeUuidPrefix(query, selectionModeCode);
                const detectedQueryLang =
                    detectLangKeyFromText(actualQuery) || 'english';

                const dynamicUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/run-customer-qna`
                );

                const hasUploads = Array.isArray(uploadedFiles) && uploadedFiles.length > 0;

                const formData = new FormData();
                formData.append('token', props.user.token);
                formData.append('query', query);
                formData.append('productCategory', effectiveProductCategory || '');
                formData.append('product', effectiveProduct || '');
                formData.append('language', detectedQueryLang || 'english');
                formData.append('enabled', 'true');
                formData.append('sessionId', String(sessionId));
                formData.append('interface', 'product_support');
                if (supportRepoId !== null && supportRepoId !== undefined) {
                    formData.append('repoId', String(supportRepoId));
                }
                if (virtualRepoId !== null && virtualRepoId !== undefined) {
                    formData.append('virtualRepoId', String(virtualRepoId));
                }
                if (hasUploads) {
                    uploadedFiles.forEach((f) => {
                        formData.append('uploaded_files', f);
                    });
                }

                const qnaResponse = await axios.post(dynamicUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    signal: controller.signal,
                });

                const data = qnaResponse.data;

                if (data) {
                    const response = data.response;
                    const metadata = data.metadata;
                    
                    // Check if response contains error message (from external interface errors)
                    if (response.answer && response.answer === ErrorMessages.GENERIC_ERROR) {
                        // Display error as antd alert at top, don't add to chat
                        alert(AlertType.ERROR, response.answer);
                        return;
                    }
                    
                    // Auto-detected list/chart presentation for a plain-language answer
                    // (see auto_format on run-customer-qna). Table is handled generically
                    // already via response.table below - AnswerView renders it whenever
                    // present, regardless of type. responseString (response.answer) stays
                    // the plain-text answer for chat history; only the display copy changes.
                    let displayAnswer = response.answer;
                    let displayType;
                    if (response.format_type === ChatFormatType.LIST && response.list_data) {
                        displayType = ChatFormatType.LIST;
                        displayAnswer = { listData: response.list_data };
                    } else if (response.format_type === ChatFormatType.CHART && response.chart_data) {
                        displayType = ChatFormatType.CHART;
                        displayAnswer = {
                            columns: response.chart_data.columns,
                            data: response.chart_data.data,
                            chartType: response.chart_data.chart_type,
                            options: {
                                title: response.chart_data.title,
                                hAxis: { title: response.chart_data.x_axis_title },
                                vAxis: { title: response.chart_data.y_axis_title },
                            },
                        };
                    }

                    const questionData = {
                        qid: data.qid,
                        question: actualQuery,
                        answer: displayAnswer,
                        ...(displayType && { type: displayType }),
                        table: response.table,
                        timestamp: metadata.timestamp,
                        chunksData: response.chunks ? [] : null,
                        queries: [],
                        productCategory: productCategory,
                        product: product,
                        sqlQuery: metadata.sqlQuery,
                        selectionMode: metadata.needMoreUserInput,
                        attachments: metadata?.attachments || [],
                        displayRelatedDoc: data.display_related_doc ?? true,
                    };

                    setSelectionMode(metadata.needMoreUserInput);

                    response.chunks?.forEach((chunk) => {
                        const chunkData = {
                            folderName: chunk.repositoryName,
                            fileName: chunk.document,
                            actualFileName: chunk.document,
                            pageNumber: chunk.pageNumber,
                            pageContent: chunk.content,
                            startLineNumber: chunk.startLineNumber,
                            rank: chunk.rank ?? 0,
                            associated_website: chunk.associated_website || null,
                        };

                        questionData.chunksData.push(chunkData);
                    });

                    // Sort chunks by ranked documents then page wise for same document
                    const pageWiseChunks = sortChunks(
                        questionData.chunksData,
                        SortSameDocument.PAGE_WISE
                    );

                    questionData.chunksData = pageWiseChunks;

                    questionsData.set(data.qid, questionData);

                    setQuestionsData(questionsData);
                    setActiveQuestion(questionData);

                    const qna = {
                        question: actualQuery,
                        answer: response.answer,
                        qid: data.qid,
                        productCategory: productCategory,
                        product: product,
                    };
                    setcurrentqna([qna]);

                    setQuery('');
                    if (hasUploads) {
                        setUploadedFiles([]);
                    }

                    // Auto-detect language from answer text for TTS
                    const answerPlainText = htmlToPlainText(response.answer);
                    const detectedAnswerLang =
                        detectLangKeyFromText(answerPlainText) ||
                        detectedQueryLang ||
                        'english';

                    // Call TTS API for speech output
                    const audioURLArray = await processTextToAudioChunks(
                        answerPlainText,
                        detectedAnswerLang
                    );

                    const updatedAudioUrlData = { ...audioUrlData };
                    updatedAudioUrlData[data.qid] = audioURLArray;
                    setAudioUrlData(updatedAudioUrlData);

                    setActiveAudioId(data.qid);
                } else if (data.error || data.detail) {
                    alert(
                        AlertType.ERROR,
                        `Error: ${data.error || data.detail}`
                    );
                }
            } catch (error) {
                if (error.name === ErrorType.ABORT_ERROR) {
                    if (error.name === ErrorType.CANCEL_ERROR) {
                        const message =
                            'Request cancelled due to page change while processing';
                        alert(AlertType.WARNING, message);
                    }
                } else {
                    const respData = error?.response?.data || {};
                    const errorDetail = respData?.detail;
                    const errorText = respData?.error || errorDetail;

                    if (
                        errorDetail === 'invalid token' ||
                        respData?.error === 'invalid token'
                    ) {
                        // Session has expired or captcha token is invalid.
                        logout(
                            vendorId,
                            setIsGenerating,
                            props.setAuthState,
                            navigate,
                            enableAudioFeature
                        );
                    } else if (errorText) {
                        alert(AlertType.ERROR, errorText);
                    } else {
                        alert(AlertType.ERROR, ErrorMessages.GENERIC_ERROR);
                    }
                }
            } finally {
                setIsGenerating(false);
                setActiveQnaQid(null);
                setQnaLoadingText('');
            }
        },
        [
            query,
            uploadedFiles,
            questionsData,
            audioUrlData,
            currentqna,
            selectedProductCategory,
            selectedProduct,
            supportRepoId,
            supportVirtualRepoId,
            vendorDetails,
            enableAudioFeature,
            selectionMode,
            props,
            vendorId,
        ]
    );

    // Effect to handle repository change
    useEffect(() => {
        if (abortController) {
            // Abort the previous request
            abortController.abort();
        }
        // Clear the controller after aborting
        setAbortController(null);
    }, []); // Run this effect whenever repo changes

    useEffect(() => {
        const handleBeforeUnload = () => {
            // Reset togglePdfVisibility here
            props.togglePdfVisibility();
        };

        // Add event listener for beforeunload event
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []); // Empty dependency array to ensure this effect runs only once on mount

    useEffect(() => {
        // Reset both states to false when the component mounts or updates
        props.setShowPdf(false);
        props.setOnButton(false);
    }, []);

    // Check if all chunks are from URL-based markdown files (have associated_website)
    const hasChunksData = activeQuestion && activeQuestion.chunksData && activeQuestion.chunksData.length !== 0;
    const areAllChunksFromUrls = hasChunksData && activeQuestion.chunksData.every(
        chunk => chunk.associated_website
    );

    const displayDocument =
        hasChunksData && activeQuestion.displayRelatedDoc !== false;

    return (
        <div className='h-full w-full'>
            <>
                <div className='flex items-center justify-center px-0 z-0 h-[89vh]'>
                    <div className='w-full h-full'>
                        {displayDocument ? (
                            <ReflexContainer orientation='vertical'>
                                {!props.showPdf && (
                                    <ReflexElement className='left-pane'>
                                        <div className='pane-content pr-0.5 w-full h-full bg-transparent'>
                                            <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                                <ChatContainer
                                                    activeQuestion={
                                                        activeQuestion
                                                    }
                                                    isGenerating={isGenerating}
                                                    setIsGenerating={
                                                        setIsGenerating
                                                    }
                                                    questionsData={
                                                        questionsData
                                                    }
                                                    query={query}
                                                    currentQnA={currentqna}
                                                    user={props.user}
                                                    setQuery={setQuery}
                                                    handleSubmit={handleSubmit}
                                                    uploadedFiles={uploadedFiles}
                                                    setUploadedFiles={setUploadedFiles}
                                                    qnaLoadingText={qnaLoadingText}
                                                    togglePdfVisibility={
                                                        props.togglePdfVisibility
                                                    }
                                                    selectedProductCategory={
                                                        selectedProductCategory
                                                    }
                                                    selectedProduct={
                                                        selectedProduct
                                                    }
                                                    isSmallScreen={
                                                        isSmallScreen
                                                    }
                                                    enableAudioFeature={
                                                        enableAudioFeature
                                                    }
                                                    selectionMode={
                                                        selectionMode
                                                    }
                                                    hasProductCategories={
                                                        hasProductCategories
                                                    }
                                                    hasProducts={hasProducts}
                                                    enableReader={enableReader}
                                                    setEnableReader={
                                                        setEnableReader
                                                    }
                                                    enableMusic={enableMusic}
                                                    setEnableMusic={
                                                        setEnableMusic
                                                    }
                                                    enableSpeaker={
                                                        enableSpeaker
                                                    }
                                                    setEnableSpeaker={
                                                        setEnableSpeaker
                                                    }
                                                    activeAudioId={
                                                        activeAudioId
                                                    }
                                                    setActiveAudioId={
                                                        setActiveAudioId
                                                    }
                                                    audioUrlData={audioUrlData}
                                                    vendorDetails={
                                                        vendorDetails
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </ReflexElement>
                                )}

                                {!isSmallScreen && (
                                    <ReflexSplitter
                                        style={{
                                            border: 0,
                                            background: 'transparent',
                                            width: '6px',
                                            zIndex: 0,
                                        }}
                                    />
                                )}

                                {/* Render PDF view based on showPdf */}
                                {(props.showPdf ||
                                    props.onButton ||
                                    !isSmallScreen) && (
                                    <ReflexElement className='right-pane'>
                                        <div className='pane-content pl-0.5 w-full h-full bg-transparent'>
                                            <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                                <DocumentContainer
                                                    user={props.user}
                                                    activeQuestion={
                                                        activeQuestion
                                                    }
                                                    togglePdfVisibility={
                                                        props.togglePdfVisibility
                                                    }
                                                    isSmallScreen={
                                                        isSmallScreen
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </ReflexElement>
                                )}
                            </ReflexContainer>
                        ) : (
                            <ReflexContainer orientation='vertical'>
                                <ReflexElement className='middle-pane'>
                                    <div className='pane-content w-full h-full bg-transparent'>
                                        <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                            <ChatContainer
                                                activeQuestion={activeQuestion}
                                                isGenerating={isGenerating}
                                                setIsGenerating={
                                                    setIsGenerating
                                                }
                                                questionsData={questionsData}
                                                query={query}
                                                currentQnA={currentqna}
                                                user={props.user}
                                                setQuery={setQuery}
                                                handleSubmit={handleSubmit}
                                                uploadedFiles={uploadedFiles}
                                                setUploadedFiles={setUploadedFiles}
                                                qnaLoadingText={qnaLoadingText}
                                                togglePdfVisibility={
                                                    props.togglePdfVisibility
                                                }
                                                selectedProductCategory={
                                                    selectedProductCategory
                                                }
                                                selectedProduct={
                                                    selectedProduct
                                                }
                                                isSmallScreen={isSmallScreen}
                                                enableAudioFeature={
                                                    enableAudioFeature
                                                }
                                                hasProductCategories={
                                                    hasProductCategories
                                                }
                                                hasProducts={hasProducts}
                                                enableReader={enableReader}
                                                setEnableReader={
                                                    setEnableReader
                                                }
                                                enableMusic={enableMusic}
                                                setEnableMusic={setEnableMusic}
                                                enableSpeaker={enableSpeaker}
                                                setEnableSpeaker={
                                                    setEnableSpeaker
                                                }
                                                activeAudioId={activeAudioId}
                                                setActiveAudioId={
                                                    setActiveAudioId
                                                }
                                                audioUrlData={audioUrlData}
                                                vendorDetails={vendorDetails}
                                                selectionMode={selectionMode}
                                            />
                                        </div>
                                    </div>
                                </ReflexElement>
                            </ReflexContainer>
                        )}
                    </div>
                </div>
            </>
        </div>
    );
};

export default ChatAndDocumentContainer;
